import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import { AiOutlineEdit } from 'react-icons/ai';

import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import { Trans, useTranslation } from 'react-i18next';
import { deleteDataIntegrationApi, getDataIntegrationsApi } from './integrationApi';
import { IoTrashOutline } from 'react-icons/io5';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import { formatDate, transErrorMessage } from '../../utilities/common';
import { toast } from 'react-toastify';
import { FiAlertCircle } from 'react-icons/fi';
import ErrorDetailModal from '../../components/ErrorDetailModal';

const ManageDataIntegration = () => {
  const { t } = useTranslation();
  const match = useRouteMatch();
  const orgId = useSelector((state) => state.users.user.usrOrgId);
  const [integrations, setIntegrations] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchIntegrations = useCallback(async () => {
    const data = await getDataIntegrationsApi(orgId);
    setIntegrations(data);
  }, [orgId]);

  const handleDeleteAction = (integration) => {
    setDeletingItem(integration);
  };

  const deleteDataIntegration = async () => {
    setIsLoading(true);
    try {
      await deleteDataIntegrationApi(orgId, deletingItem.id);
      await fetchIntegrations();
    } catch (err) {
      toast.error(transErrorMessage(t, err));
    } finally {
      setDeletingItem(null);
      setIsLoading(false);
    }
  };

  const tableColumns = useMemo(
    () => [
      {
        Header: t('Name'),
        accessor: 'name',
        sortType: 'string'
      },
      {
        Header: t('Data Type'),
        accessor: 'dataType',
        sortType: 'string'
      },
      {
        Header: t('Connection'),
        accessor: 'connectType',
        sortType: 'string'
      },
      {
        Header: t('Remote Dir'),
        accessor: 'remoteDir',
        sortType: 'string'
      },
      {
        Header: t('Status'),
        accessor: (row) => row.activated ? 'Active' : 'Inactive',
      },
      {
        Header: t('Data Received At'),
        accessor: (row) => row.dataReceivedAt ? formatDate(row.dataReceivedAt) : t('N/A'),
      },
      {
        Header: t('Created At'),
        id: 'createdAt',
        accessor: (row) => formatDate(row.createdAt),
      },
      {
        Header: '',
        accessor: 'actions',
        actionColumn: true,
        disableGlobalFilter: false,
        disableSortBy: true,
        Cell: ({ row }) => {
          const { original } = row;
          return (
            <div className="action-buttons">
              <Button
                to={`${match.url}/${original.id}/edit`}
                size="sm"
                className="mr-1"
                variant="outline-secondary"
                as={Link}
              >
                <AiOutlineEdit />
              </Button>
              <Button variant="outline-secondary" className="mr-3" onClick={() => handleDeleteAction(original)}>
                <IoTrashOutline />
              </Button>
              {!!original.errorMessage &&
                <Button variant="outline-secondary" onClick={() => setErrorMessage(original.errorMessage)}>
                  <FiAlertCircle style={{ color: '#f00808' }} className="mr-3" />
                </Button>
              }
            </div>
          );
        }
      }
    ],
    [match.url, t]
  );

  useEffect(() => {
    if (orgId) fetchIntegrations();
  }, [orgId, fetchIntegrations]);

  return (
    <Container fluid>
      <Row>
        <Col>
          <PageHeader
            title={t('Manage Data Integrations')}
            actions={
              <>
                <Button to={`${match.url}/new`} as={Link} className="mr-2">
                  + {t('New Data Integration')}
                </Button>
              </>
            }
          />
        </Col>
      </Row>
      {integrations === null && <Spinner animation="grow" />}
      {integrations !== null && (
        <Row>
          <Col>
            <DataTable columns={tableColumns} data={integrations} defaultSort="createdAt" />
          </Col>
        </Row>
      )}
      <DeleteConfirmDialog
        visible={deletingItem !== null}
        headerTitle={t('Delete Data Integration')}
        onDiscardHandler={() => setDeletingItem(null)}
        onDeleteHandler={deleteDataIntegration}
        isLoading={isLoading}
      >
        <p>
          <Trans
            i18nKey="Do you really want to delete the data integration <bold>{{name}}</bold>? This action cannot be undone."
            values={{ name: deletingItem?.name }}
            components={{ bold: <strong /> }}
          />
        </p>
      </DeleteConfirmDialog>
      <ErrorDetailModal visible={!!errorMessage} onDiscardHandler={() => setErrorMessage(null)}>
        <p>{errorMessage}</p>
      </ErrorDetailModal>
    </Container>
  );
};

export default ManageDataIntegration;
